import React, { useMemo, useState } from "react";
import axios from "axios";
import { Button, Form, Spinner, Alert } from "react-bootstrap";

const DEFAULT_VALIDATE_PO_EXCEL_URL =
  "https://backend-nia.caigroup.ai/validateinvoicesai_v2";

function formatBytes(bytes) {
  if (typeof bytes !== "number" || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(1)} GB`;
}

export default function ValidatePOExcelPage() {
  const validateUrl = useMemo(
    () =>
      (
        process.env.REACT_APP_VALIDATE_PO_EXCEL_URL ||
        DEFAULT_VALIDATE_PO_EXCEL_URL
      ).trim(),
    []
  );

  const [poMasterFile, setPoMasterFile] = useState(null);
  const [outputFile, setOutputFile] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = Boolean(poMasterFile && outputFile && !loading);

  const handleSubmit = async () => {
    setError("");

    if (!poMasterFile || !outputFile) {
      setError(
        "Please upload both PO Master and Output files before submitting."
      );
      return;
    }

    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const requestId =
      `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${pad(
        now.getFullYear() % 100
      )}-` +
      `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(
        now.getSeconds()
      )}`;

    const formData = new FormData();
    formData.append("po_master", poMasterFile);
    formData.append("extracted_invoice", outputFile);
    formData.append("user_prompt", prompt);
    formData.append("request_id", requestId);

    setLoading(true);
    try {
      const res = await axios.post(validateUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
      });

      // Normalize response for display/download
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "ValidationResult.xlsx";
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
      //const text =
      //  typeof data === "string" ? data : JSON.stringify(data, null, 2);
      //setResponseText(text);
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        "Request failed.";
      setError(
        `Failed to validate Excel files. ${msg}${
          validateUrl ? ` (URL: ${validateUrl})` : ""
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12 ">
          <div className="whiteBox">
            <div className="row margin70px">
              <div className="col-md-12">
                <h1 className="title">1. Upload the Excel files</h1>
              </div>
              <div className="col-md-6">
                <Form.Group className="chooseFile">
                  <Form.Label className="fileLabel">PO Master File</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) =>
                      setPoMasterFile(e.target.files?.[0] || null)
                    }
                  />
                  {poMasterFile && (
                    <div className="fileMeta">
                      <span>{poMasterFile.name}</span>
                      <span className="fileMetaSep">•</span>
                      <span>{formatBytes(poMasterFile.size)}</span>
                    </div>
                  )}
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="chooseFile">
                  <Form.Label className="fileLabel">Output File</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setOutputFile(e.target.files?.[0] || null)}
                  />
                  {outputFile && (
                    <div className="fileMeta">
                      <span>{outputFile.name}</span>
                      <span className="fileMetaSep">•</span>
                      <span>{formatBytes(outputFile.size)}</span>
                    </div>
                  )}
                </Form.Group>
              </div>
            </div>

            <div className="row align-items-end" style={{ marginBottom: 30 }}>
              <div className="col-md-8">
                <div className="promptWrp">
                  <div>
                    <h1 className="title">2. Add a prompt</h1>
                  </div>
                </div>
                <Form.Group>
                  <Form.Control
                    id="validate-po-excel-prompt"
                    as="textarea"
                    rows={3}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Enter validation instructions or matching rules..."
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                {error && (
                  <Alert variant="danger" style={{ marginBottom: 10 }}>
                    {error}
                  </Alert>
                )}
                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="saveBtn"
                  style={{ width: "100%" }}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" /> Processing...
                    </>
                  ) : (
                    "Validate"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
